package models

/** The move a bot makes on its turn. */
sealed interface BotAction {
    /** Roll the dice, pushing the pot higher (and risking a bust). */
    data object Roll : BotAction

    /** Lock in the current pot for the bot itself. */
    data object Bank : BotAction
}

/**
 * Decides what a bot does on its turn. Pure and deterministic given the game state, so it can be
 * unit-tested in isolation and reused by any driver (currently the local game loop).
 *
 * Strategy:
 *  - During the first three rolls a 7 is high-value and cannot bust the pot, so rolling is free upside.
 *  - After that, every roll carries a 1-in-6 chance of a 7 wiping the pot, so the bot locks in once
 *    the pot is worth protecting.
 *
 * The bank threshold is the single knob that defines difficulty — a lower threshold is a more timid
 * (weaker) opponent, a higher one rides the variance longer. Difficulty tiers can later be layered
 * on by passing different thresholds per bot.
 */
object BankoBot {
    const val DEFAULT_BANK_THRESHOLD = 25

    /** Rolls 1..SAFE_ROLLS cannot bust on a 7, so the bot always rolls through them. */
    private const val SAFE_ROLLS = 3

    fun decide(game: DomainGame, bankThreshold: Int = DEFAULT_BANK_THRESHOLD): BotAction {
        val round = game.round ?: return BotAction.Roll

        // Some twists forbid banking; the only legal move is to roll.
        if (round.modifier.banksLockedAtRoll(round.currentRoll) || round.ginkoEvent.locksBanking) {
            return BotAction.Roll
        }

        if (round.ginkoEvent == GinkoEvent.HEIST_WINDOW || round.ginkoEvent == GinkoEvent.LAST_CALL) {
            return BotAction.Bank
        }

        // The first three rolls can't bust under any modifier (Flipside's doubles-bust only kicks in
        // after the safe rolls, like the normal 7-bust), so rolling early is always free.
        if (round.currentRoll <= SAFE_ROLLS) {
            return BotAction.Roll
        }

        return if (round.currentPoints >= bankThreshold) BotAction.Bank else BotAction.Roll
    }
}
